import React, { memo, useCallback, useMemo } from 'react'

import { classnames, Icon } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, string } from 'prop-types'

import DesktopOnlyTooltip from 'Pods/Shared/DesktopOnlyTooltip/DesktopOnlyTooltip'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

import { NOOP } from 'Utils/stableDefaults'

const iconSize = {
  height: '20px',
  width: '20px',
}

function ViewerSidebarItem({
  id,
  label = undefined,
  className = undefined,
  onClick: handleClick = NOOP,
  isDisabled = false,
  hideDisabledTooltip = false,
  disabledReason = undefined,
  icon,
  isStatic = false,
  badgeCount = undefined,
}) {
  const t = useTranslation()
  const resolvedDisabledReason = disabledReason ?? t('viewerSidebar.nav.disabledReason')
  const isActive = useThreeDStudioStore(
    state => !isStatic && state.ui?.activeViewerSidebarTab === id,
  )

  const iconComponent = useMemo(() => {
    return icon && (
      <Icon
        svgComponent={icon}
        color={isActive ? 'primary' : 'darker'}
        size={iconSize}
        notSpaced
      />
    )
  }, [icon, isActive])

  const handleItemClick = useCallback((event) => {
    if (isDisabled) {
      event?.preventDefault()
      return
    }

    handleClick(event)
  }, [handleClick, isDisabled])

  const handleKeyDown = useCallback((event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    handleItemClick(event)
  }, [handleItemClick])

  const showBadge = badgeCount > 0
  const showDisabledReason = isDisabled && !hideDisabledTooltip
  const labelWithCount = showBadge && label ? `${label} (${badgeCount})` : label
  const ariaLabel = showDisabledReason && labelWithCount
    ? `${labelWithCount}. ${resolvedDisabledReason}`
    : labelWithCount

  const item = (
    <div
      role={isStatic ? undefined : 'button'}
      tabIndex={isStatic ? undefined : 0}
      aria-disabled={isStatic ? undefined : isDisabled}
      aria-label={isStatic ? undefined : ariaLabel}
      className={classnames(
        'viewer-sidebar__item',
        isActive && 'viewer-sidebar__item--active',
        isDisabled && 'viewer-sidebar__item--disabled',
        className,
      )}
      onClick={isStatic ? undefined : handleItemClick}
      onKeyDown={isStatic ? undefined : handleKeyDown}
    >
      <div className="viewer-sidebar__item-content">
        <span className="viewer-sidebar__item-icon">
          {iconComponent}
        </span>
        {label && (
          <span className="viewer-sidebar__item-label">
            {label}
          </span>
        )}
        {showBadge && (
          <span
            className="viewer-sidebar__item-badge cgt-caption-semibold cgt-caption-semibold--dark-bg"
            aria-hidden="true"
          >
            {badgeCount}
          </span>
        )}
      </div>
    </div>
  )

  if (!showDisabledReason) {
    return item
  }

  return (
    <DesktopOnlyTooltip
      content={resolvedDisabledReason}
      id={`viewer-sidebar-${id}-disabled-tooltip`}
      place="left"
    >
      {item}
    </DesktopOnlyTooltip>
  )
}

ViewerSidebarItem.propTypes = {
  id: string.isRequired,
  label: string,
  className: string,
  onClick: func,
  isDisabled: bool,
  hideDisabledTooltip: bool,
  disabledReason: string,
  icon: func.isRequired,
  isStatic: bool,
  badgeCount: number,
}

export default memo(ViewerSidebarItem)
